import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { FilterOptions, LogsSearchOptions } from '../models/filter.model';
import {
  Order,
  OrderDetails,
  HealthCertificateDetails,
  OrderWithStages,
  OrderDynamicFieldValues,
  OrderLogs,
  ForwardStatementAttachment,
} from '../models/order.model';
import { OrderStage } from '../enums/order-stage';
import { OrderApprovalParty } from '../enums/order-approval-party';
import { ResponseData } from '../models/helpers.model';
import {
  OrderItemTranslations,
  SetOrderItemTranslationEntry,
} from '../models/item-language.model';
import {
  OrderDocScan,
  OrderDocumentForStages,
  OrderDocumentPreview,
} from '../models/order-document';
import { User } from '../models/user.model';

@Injectable({
  providedIn: 'root',
})
export class OrdersService {
  constructor(private http: HttpClient) {}

  getOrders(filterOptions: FilterOptions): Observable<ResponseData<Order>> {
    return this.http.post<ResponseData<Order>>(`/orders/filter`, filterOptions);
  }

  getOrdersSummary(filterOptions: FilterOptions): Observable<number> {
    return this.http.post<number>(`/orders/filter/summary`, filterOptions);
  }

  getOrder(id: string): Observable<OrderWithStages> {
    return this.http.get<OrderWithStages>(`/orders/${id}`);
  }

  getLogs(id: string, searchData: LogsSearchOptions): Observable<OrderLogs> {
    return this.http.post<OrderLogs>(
      `/orders/${id}/change-logs/filter`,
      searchData
    );
  }

  getUsersForLogs(id: string): Observable<User[]> {
    return this.http.get<User[]>(`/orders/${id}/change-logs/users`);
  }

  setOrderIsNew(id: string, data: { isNew: boolean }): Observable<string> {
    return this.http.put<string>(`/orders/${id}/set-is-new`, data, {
      responseType: 'text' as 'json',
    });
  }

  setShipper(id: string, data: { shipperId: string }): Observable<string> {
    return this.http.put<string>(`/orders/${id}/set-shipper`, data, {
      responseType: 'text' as 'json',
    });
  }

  setBuyer(id: string, data: { buyerId: string }): Observable<string> {
    return this.http.put<string>(`/orders/${id}/set-buyer`, data, {
      responseType: 'text' as 'json',
    });
  }

  setConsignee(id: string, data: { consigneeId: string }): Observable<string> {
    return this.http.put<string>(`/orders/${id}/set-consignee`, data, {
      responseType: 'text' as 'json',
    });
  }

  setDetails(id: string, data: OrderDetails): Observable<string> {
    return this.http.put<string>(`/orders/${id}/set-details`, data, {
      responseType: 'text' as 'json',
    });
  }

  setSpecialInstruction(
    id: string,
    data: { specialInstruction: string }
  ): Observable<string> {
    return this.http.put<string>(
      `/orders/${id}/set-special-instruction`,
      data,
      {
        responseType: 'text' as 'json',
      }
    );
  }

  setAdditionalCertification(
    id: string,
    data: { additionalCertification: string }
  ): Observable<string> {
    return this.http.put<string>(
      `/orders/${id}/set-additional-certification`,
      data,
      {
        responseType: 'text' as 'json',
      }
    );
  }

  setCancel(id: string, canceledReason: string): Observable<string> {
    return this.http.put<string>(
      `/orders/${id}/set-cancel`,
      { canceledReason },
      {
        responseType: 'text' as 'json',
      }
    );
  }

  // ECDEV-159: reopen an auto-closed order (admin only).
  // ECDEV-324: the admin supplies the new ETS — a future 'YYYY-MM-DD' date, or null to clear it.
  // The backend sends the order back to Stage 1.
  reopen(id: string, ets: string | null): Observable<string> {
    return this.http.put<string>(`/orders/${id}/reopen`, { ets }, {
      responseType: 'text' as 'json',
    });
  }

  // ECDEV-158: Stage 4 "Close Order" — close by hand instead of waiting for the ETS date.
  closeOrder(id: string): Observable<string> {
    return this.http.put<string>(`/orders/${id}/close`, {}, {
      responseType: 'text' as 'json',
    });
  }

  // ECDEV-160: Stage 4 "Docs Printed" — the final documents are printed and on their way, so
  // the order stops accepting edits and the customer is notified.
  markDocsPrinted(id: string): Observable<string> {
    return this.http.put<string>(`/orders/${id}/docs-printed`, {}, {
      responseType: 'text' as 'json',
    });
  }

  // ECDEV-160: lift the Docs Printed lock so the order can be corrected (admin only).
  unlockDocsPrinted(id: string): Observable<string> {
    return this.http.put<string>(`/orders/${id}/docs-printed/unlock`, {}, {
      responseType: 'text' as 'json',
    });
  }

  // ECDEV-158: Stage 4 "Reset to Stage 3" — back to the start of Stage 3 (admin only).
  resetToStage3(id: string): Observable<string> {
    return this.http.put<string>(`/orders/${id}/reset-to-stage3`, {}, {
      responseType: 'text' as 'json',
    });
  }

  setForwardStatementAttachment(
    id: string,
    data: { forwardStatementAttachmentId: string }
  ): Observable<string> {
    return this.http.put<string>(
      `/orders/${id}/set-forward-statement-attachment`,
      data,
      {
        responseType: 'text' as 'json',
      }
    );
  }

  // ECDEV-169: attachment ids the current user may delete on this order (admin, or uploader within window).
  getDeletableAttachments(orderId: string): Observable<string[]> {
    return this.http.get<string[]>(
      `/orders/${orderId}/attachments/deletable`
    );
  }

  // ECDEV-169: delete an order attachment (server enforces admin / uploader+window; audited).
  deleteOrderAttachment(
    orderId: string,
    attachmentId: string,
    stage: string
  ): Observable<string> {
    return this.http.delete<string>(
      `/orders/${orderId}/attachments/${attachmentId}?stage=${encodeURIComponent(
        stage
      )}`,
      { responseType: 'text' as 'json' }
    );
  }

  // ECDEV-248: item translations for the order form — pre-filled from the order's own
  // values, then the customer library, each flagged with its source.
  getOrderItemTranslations(id: string): Observable<OrderItemTranslations[]> {
    return this.http.get<OrderItemTranslations[]>(
      `/orders/${id}/item-translations`
    );
  }

  // Writes the order values and upserts the customer library in one transaction.
  setOrderItemTranslations(
    id: string,
    entries: SetOrderItemTranslationEntry[]
  ): Observable<string> {
    return this.http.put<string>(
      `/orders/${id}/item-translations`,
      { entries },
      { responseType: 'text' as 'json' }
    );
  }

  // ECDEV-163: Cold Storage updates Date of Dispatch without resetting approvals or notifying.
  setOrderDispatchDate(
    id: string,
    dateOfDispatch: string | null
  ): Observable<string> {
    return this.http.put<string>(
      `/orders/${id}/dispatch-date`,
      { dateOfDispatch },
      {
        responseType: 'text' as 'json',
      }
    );
  }

  // ECDEV-162: list / append multiple Stage-1 forward-statement attachments.
  getForwardStatementAttachments(
    id: string
  ): Observable<ForwardStatementAttachment[]> {
    return this.http.get<ForwardStatementAttachment[]>(
      `/orders/${id}/forward-statement-attachments`
    );
  }

  addForwardStatementAttachments(
    id: string,
    ids: string[]
  ): Observable<string> {
    return this.http.post<string>(
      `/orders/${id}/forward-statement-attachments`,
      { ids },
      {
        responseType: 'text' as 'json',
      }
    );
  }

  setHealthCertificateDetails(
    id: string,
    data: Partial<HealthCertificateDetails>
  ): Observable<string> {
    return this.http.put<string>(
      `/orders/${id}/set-health-certificate-details`,
      data,
      {
        responseType: 'text' as 'json',
      }
    );
  }

  setDynamicFieldsValue(
    id: string,
    data: { fields: Array<OrderDynamicFieldValues>; fromStage: OrderStage }
  ): Observable<boolean> {
    return this.http.put<boolean>(`/orders/${id}/fields`, data);
  }

  setCertificateNumber(
    id: string,
    data: { number: string; reason: string }
  ): Observable<string> {
    return this.http.put<string>(`/orders/${id}/set-certificate-number`, data, {
      responseType: 'text' as 'json',
    });
  }

  // Assigns an HC number chosen from an admin-defined range (Stage 2B initial set).
  // Server validates range membership + used-once.
  assignCertificateNumber(id: string, number: string): Observable<string> {
    return this.http.put<string>(
      `/orders/${id}/assign-certificate-number`,
      { number },
      { responseType: 'text' as 'json' }
    );
  }

  setOrderNotificationIsSeen(
    orderId: string,
    notificationId: string
  ): Observable<string> {
    return this.http.put<string>(
      `/orders/${orderId}/notifications/${notificationId}/set-is-seen`,
      {},
      {
        responseType: 'text' as 'json',
      }
    );
  }

  setOrderRelease(
    id: string,
    data: { id: string; userId: string }
  ): Observable<string> {
    return this.http.put<string>(`/orders/${id}/set-release`, data, {
      responseType: 'text' as 'json',
    });
  }

  getOrderDocuments(
    id: string
  ): Observable<HttpResponse<OrderDocumentForStages[]>> {
    return this.http.get<OrderDocumentForStages[]>(`/orders/${id}/documents`, {
      observe: 'response',
    });
  }

  // ECDEV-174: regenerates both variants of the document (with / without the pre-printed
  // background) and reports the caller's remembered print-layout choice for this template.
  // Previously returned only the with-background attachment id.
  getOrderDocumentPreview(
    orderId: string,
    docId: string
  ): Observable<OrderDocumentPreview> {
    return this.http.get<OrderDocumentPreview>(
      `/orders/${orderId}/documents/${docId}/preview`
    );
  }

  // ECDEV-253: record that this document was sent to the printer, so Stage 4 can mark it in the
  // document list. Answers with the updated row.
  markOrderDocumentPrinted(
    orderId: string,
    docId: string
  ): Observable<OrderDocumentForStages> {
    return this.http.put<OrderDocumentForStages>(
      `/orders/${orderId}/documents/${docId}/printed`,
      {}
    );
  }

  // ECDEV-253: record that it was downloaded instead — the file left the system and may have been
  // printed from the reader, where we cannot see it.
  markOrderDocumentDownloaded(
    orderId: string,
    docId: string
  ): Observable<OrderDocumentForStages> {
    return this.http.put<OrderDocumentForStages>(
      `/orders/${orderId}/documents/${docId}/downloaded`,
      {}
    );
  }

  setOrderDocumentStatus(
    orderId: string,
    docId: string,
    data: { isApproved: boolean; rejectReason: string | null; role: number }
  ): Observable<OrderDocumentForStages> {
    return this.http.put<OrderDocumentForStages>(
      `/orders/${orderId}/documents/${docId}/set-status`,
      data
    );
  }

  // ECDEV-373: take one document off this order (admin only).
  excludeOrderDocument(orderId: string, docId: string): Observable<string> {
    return this.http.delete<string>(`/orders/${orderId}/documents/${docId}`, {
      responseType: 'text' as 'json',
    });
  }

  uploadDocumentScan(
    orderId: string,
    docId: string,
    scanId: string
  ): Observable<string> {
    let params = new HttpParams().set('attachmentId', scanId);
    return this.http.post<string>(
      `/orders/${orderId}/documents/${docId}/scan`,
      {},
      {
        params: params,
        responseType: 'text' as 'json',
      }
    );
  }

  getOrderPhotos(orderId: string): Observable<string[]> {
    return this.http.get<string[]>(`/orders/${orderId}/photos`);
  }

  uploadOrderPhotos(
    orderId: string,
    data: { ids: Array<string> }
  ): Observable<string> {
    return this.http.post<string>(`/orders/${orderId}/photos`, data, {
      responseType: 'text' as 'json',
    });
  }

  getDocumentScans(orderId: string): Observable<OrderDocScan[]> {
    return this.http.get<OrderDocScan[]>(`/orders/${orderId}/scans`);
  }

  uploadDocumentScans(
    orderId: string,
    data: { attachmentIds: Array<string> }
  ): Observable<string> {
    return this.http.post<string>(`/orders/${orderId}/scans`, data, {
      responseType: 'text' as 'json',
    });
  }

  cmpInventoryApproveStage3(id: string): Observable<string> {
    return this.http.put<string>(
      `/orders/${id}/stage3/cmp-inventory-approve`,
      {},
      { responseType: 'text' as 'json' }
    );
  }

  customerApproveStage3(id: string): Observable<string> {
    return this.http.put<string>(
      `/orders/${id}/stage3/customer-approve`,
      {},
      { responseType: 'text' as 'json' }
    );
  }

  // ECDEV-331: Stage 3's Notify — only offered when no document on the order requires approval,
  // where the automatic Stage 3 → Stage 4 advance can never fire. The server re-checks that.
  completeStage3(id: string): Observable<string> {
    return this.http.put<string>(
      `/orders/${id}/stage3/complete`,
      {},
      { responseType: 'text' as 'json' }
    );
  }

  cmpInventoryApprove(id: string): Observable<string> {
    return this.http.put<string>(
      `/orders/${id}/stage4/cmp-inventory-approve`,
      {},
      { responseType: 'text' as 'json' }
    );
  }

  customerApprove(id: string, comment: string): Observable<string> {
    let params = new HttpParams().set('comment', comment);
    return this.http.put<string>(
      `/orders/${id}/stage4/customer-approve`,
      {},
      { params: params, responseType: 'text' as 'json' }
    );
  }

  // ECDEV-360: CM Admin records the Stage 4 approval of CM Inventory or of the Customer on that
  // party's behalf. Admin-only server-side; the comment is required and says what the approval
  // was for.
  approveOnBehalf(
    id: string,
    party: OrderApprovalParty,
    comment: string
  ): Observable<string> {
    return this.http.put<string>(
      `/orders/${id}/stage4/approve-on-behalf`,
      { party, comment },
      { responseType: 'text' as 'json' }
    );
  }

  sendNotificationAboutChanges(
    id: string,
    stage: OrderStage
  ): Observable<string> {
    return this.http.put<string>(
      `/orders/${id}/notifications/stage/${stage}/ready-to-review`,
      {},
      { responseType: 'text' as 'json' }
    );
  }
}
